import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LegalOrganization } from '../../types';

interface OrganizationCardProps {
  organization: LegalOrganization;
}

export default function OrganizationCard({ organization }: OrganizationCardProps) {
  const { t } = useTranslation();

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {organization.name}
            </h3>
            {organization.city && organization.state && (
              <p className="text-sm text-gray-500 mt-1">
                {organization.city}, {organization.state}
              </p>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-col gap-1">
            {organization.isVerified && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t('resources.verified')}
              </span>
            )}
            {organization.isFree && (
              <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                {t('resources.freeServices')}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {organization.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {organization.description}
          </p>
        )}

        {/* Specializations */}
        {organization.specializations.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {organization.specializations.slice(0, 4).map((spec, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
              >
                {spec}
              </span>
            ))}
            {organization.specializations.length > 4 && (
              <span className="px-2 py-0.5 text-gray-500 text-xs">
                +{organization.specializations.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Languages */}
        {organization.languages.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            {organization.languages.join(', ')}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Contact Actions */}
        <div className="flex gap-2 pt-4 border-t border-gray-100">
          {organization.phone && (
            <a
              href={`tel:${organization.phone}`}
              className="btn-primary flex-1 text-sm py-2"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {t('resources.callNow')}
            </a>
          )}
          {organization.websiteUrl && (
            <a
              href={organization.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex-1 text-sm py-2"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              {t('resources.website')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
